/**
 * SpendableCard.js — Hero metric card for spendable balance
 *
 * Dark navy card showing the core question: "How much can I actually spend?"
 *
 * Displays:
 *   - Spendable balance (checking - upcoming unpaid bills)
 *   - Days until next payday
 *   - Number of pending bills
 *   - Expandable breakdown (checking balance, upcoming bills total)
 *   - Warning if negative (short on funds)
 *   - Daily allowance below the card
 *
 * Color coding:
 *   - White/green: Healthy
 *   - Orange: Tight (< $20/day)
 *   - Red: Negative (short on funds)
 *
 * Props:
 *   result      — Output from calculateSpendable()
 *   daysUntil   — Days until next payday
 */
import React, { useState } from 'react';
import { formatCurrency } from '../utils/format';
import theme from '../utils/theme';

export default function SpendableCard({ result, daysUntil }) {
  const [showBreakdown, setShowBreakdown] = useState(false);

  if (!result) {
    return (
      <div className="spendable-card">
        <div className="spendable-label">Spendable until next payday</div>
        <div className="spendable-amount">—</div>
        <div className="spendable-meta">
          <span>Calculating...</span>
        </div>
      </div>
    );
  }

  const amountClass = result.isNegative ? 'negative' : '';

  return (
    <>
      <div
        className="spendable-card"
        onClick={() => setShowBreakdown(!showBreakdown)}
        style={{ cursor: 'pointer' }}
      >
        <div className="spendable-label">Spendable until next payday</div>
        <div className={`spendable-amount ${amountClass}`}>
          {formatCurrency(result.spendable, false)}
        </div>
        <div className="spendable-meta">
          <span>{daysUntil} days until payday</span>
          <span>{result.upcomingBillsCount} bills pending</span>
        </div>

        {/* Warning if negative */}
        {result.isNegative && (
          <div style={{
            marginTop: '0.75rem',
            padding: '0.5rem',
            background: 'rgba(231, 76, 60, 0.2)',
            borderRadius: '8px',
            fontSize: '0.8rem',
          }}>
            ⚠️ You're {formatCurrency(Math.abs(result.spendable), false)} short for upcoming bills
          </div>
        )}

        {/* Expandable breakdown */}
        {showBreakdown && (
          <div style={{
            marginTop: '0.75rem',
            paddingTop: '0.75rem',
            borderTop: '1px solid rgba(255,255,255,0.15)',
            fontSize: '0.75rem',
            opacity: 0.7,
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.25rem' }}>
              <span>Checking balance</span>
              <span>{formatCurrency(result.checkingBalance)}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.25rem' }}>
              <span>Upcoming bills ({result.upcomingBillsCount})</span>
              <span>−{formatCurrency(result.upcomingBillsTotal)}</span>
            </div>
            <div style={{
              display: 'flex', justifyContent: 'space-between',
              paddingTop: '0.35rem', borderTop: '1px solid rgba(255,255,255,0.1)',
              fontWeight: 700, opacity: 1,
            }}>
              <span>Spendable</span>
              <span>{formatCurrency(result.spendable)}</span>
            </div>

            {/* Upcoming bill details */}
            {result.upcomingBills.length > 0 && (
              <div style={{ marginTop: '0.5rem', paddingTop: '0.5rem', borderTop: '1px solid rgba(255,255,255,0.1)' }}>
                <div style={{ fontWeight: 600, marginBottom: '0.25rem', fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                  Bills before payday:
                </div>
                {result.upcomingBills.map((bill, i) => (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '0.15rem 0' }}>
                    <span>{bill.name}</span>
                    <span>{formatCurrency(bill.amount)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Daily allowance card (separate for visual weight) */}
      {!result.isNegative && (
        <div className="card" style={{ textAlign: 'center', padding: '0.75rem' }}>
          <div style={{
            fontSize: '1.5rem',
            fontWeight: 800,
            color: result.isTight ? theme.warning : theme.success,
          }}>
            {formatCurrency(result.dailyAllowance, false)}
            <span style={{ fontSize: '0.8rem', fontWeight: 400 }}>/day</span>
          </div>
          <div style={{ fontSize: '0.75rem', color: theme.textMuted }}>
            for the next {daysUntil} day{daysUntil !== 1 ? 's' : ''}
          </div>
        </div>
      )}
    </>
  );
}
